import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";

const financeApplicationSchema = z.object({
  // Company Information
  businessLegalName: z.string().min(2, "Business name is required").max(200),
  dba: z.string().max(200).optional(),
  businessAddress: z.string().min(5, "Business address is required").max(300),
  city: z.string().min(2, "City is required").max(100),
  state: z.string().min(2, "State is required").max(50),
  zip: z.string().min(5, "ZIP code is required").max(10),
  phone: z.string().min(10, "Phone number is required").max(20),
  email: z.string().email("Invalid email address").max(255),
  contactName: z.string().min(2, "Contact name is required").max(100),
  contactTitle: z.string().max(100).optional(),
  federalTaxId: z.string().min(9, "Federal Tax ID is required").max(20),
  stateOfFormation: z.string().max(50).optional(),
  dateBusinessStarted: z.string().optional(),
  currentOwnership: z.string().optional(),
  grossAnnualRevenue: z.string().optional(),
  website: z.string().max(200).optional(),
  numberOfWorkers: z.string().optional(),
  legalStructure: z.enum(["Sole Prop", "Partnership", "LLC", "Corporation", "Municipal", "Not for Profit"]),
  industryType: z.string().max(100).optional(),
  
  // Equipment & Finance Information
  equipmentDescription: z.string().min(5, "Equipment description is required").max(500),
  equipmentCost: z.string().min(1, "Equipment cost is required").max(50),
  financeAmount: z.string().min(1, "Finance amount is required").max(50),
  termInMonths: z.string().min(1, "Term is required").max(10),
  equipmentCondition: z.enum(["New", "Used"]),
  
  // Owner 1 Information
  owner1Name: z.string().min(2, "Owner name is required").max(100),
  owner1Title: z.string().max(100).optional(),
  owner1Email: z.string().email("Invalid email").max(255),
  owner1SSN: z.string().min(9, "SSN is required").max(11),
  owner1Ownership: z.string().min(1, "Ownership % is required").max(10),
  owner1Cell: z.string().min(10, "Cell phone is required").max(20),
  owner1HomePhone: z.string().max(20).optional(),
  owner1HomeAddress: z.string().min(5, "Home address is required").max(300),
  
  // Owner 2 Information (Optional)
  owner2Name: z.string().max(100).optional(),
  owner2Title: z.string().max(100).optional(),
  owner2Email: z.string().email("Invalid email").max(255).optional().or(z.literal("")),
  owner2SSN: z.string().max(11).optional(),
  owner2Ownership: z.string().max(10).optional(),
  owner2Cell: z.string().max(20).optional(),
  owner2HomePhone: z.string().max(20).optional(),
  owner2HomeAddress: z.string().max(300).optional(),
  
  // Additional Information
  additionalNotes: z.string().max(1000).optional(),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms and conditions",
  }),
});

type FinanceApplicationFormData = z.infer<typeof financeApplicationSchema>;

interface FinanceApplicationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  robotName: string;
}

export function FinanceApplicationForm({ open, onOpenChange, robotName }: FinanceApplicationFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FinanceApplicationFormData>({
    resolver: zodResolver(financeApplicationSchema),
    defaultValues: {
      businessLegalName: "",
      dba: "",
      businessAddress: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
      contactName: "",
      contactTitle: "",
      federalTaxId: "",
      stateOfFormation: "",
      dateBusinessStarted: "",
      currentOwnership: "",
      grossAnnualRevenue: "",
      website: "",
      numberOfWorkers: "",
      legalStructure: "LLC",
      industryType: "",
      equipmentDescription: robotName,
      equipmentCost: "",
      financeAmount: "",
      termInMonths: "",
      equipmentCondition: "New",
      owner1Name: "",
      owner1Title: "",
      owner1Email: "",
      owner1SSN: "",
      owner1Ownership: "",
      owner1Cell: "",
      owner1HomePhone: "",
      owner1HomeAddress: "",
      owner2Name: "",
      owner2Title: "",
      owner2Email: "",
      owner2SSN: "",
      owner2Ownership: "",
      owner2Cell: "",
      owner2HomePhone: "",
      owner2HomeAddress: "",
      additionalNotes: "",
      agreeToTerms: false,
    },
  });

  const onSubmit = async (data: FinanceApplicationFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-qualified-lead", {
        body: {
          type: "finance_application",
          robotName,
          ...data,
        },
      });

      if (error) throw error;

      toast.success("Finance application submitted successfully! Our team will contact you within 24 hours.");
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting finance application:", error);
      toast.error("Failed to submit application. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Robot Financing Application</DialogTitle>
          <DialogDescription>
            Apply for financing for your {robotName}. Fill out the form below and our financing team will review your application.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs defaultValue="company" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="company">Company</TabsTrigger>
                <TabsTrigger value="equipment">Equipment</TabsTrigger>
                <TabsTrigger value="owner1">Owner 1</TabsTrigger>
                <TabsTrigger value="owner2">Owner 2</TabsTrigger>
              </TabsList>

              <TabsContent value="company" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="businessLegalName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Business Legal Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dba"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>DBA (if applicable)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="businessAddress"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Business Street Address *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zip"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ZIP *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone *</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contactTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Title</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="federalTaxId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Federal Tax ID *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="XX-XXXXXXX" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="legalStructure"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Legal Structure *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Sole Prop">Sole Proprietorship</SelectItem>
                            <SelectItem value="Partnership">Partnership</SelectItem>
                            <SelectItem value="LLC">LLC</SelectItem>
                            <SelectItem value="Corporation">Corporation</SelectItem>
                            <SelectItem value="Municipal">Municipal</SelectItem>
                            <SelectItem value="Not for Profit">Not for Profit</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="stateOfFormation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State of Formation</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dateBusinessStarted"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date Business Started</FormLabel>
                        <FormControl>
                          <Input {...field} type="date" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="grossAnnualRevenue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gross Annual Revenue</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="$" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="numberOfWorkers"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of Workers</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="industryType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Industry Type</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input {...field} type="url" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="equipment" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="equipmentDescription"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Equipment Description *</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="equipmentCost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Equipment Cost *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="$" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="financeAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Finance Amount *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="$" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="termInMonths"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Term in Months *</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" placeholder="e.g., 36, 48, 60" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="equipmentCondition"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Equipment Condition *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="New">New</SelectItem>
                            <SelectItem value="Used">Used</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="owner1" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="owner1Name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1Title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1Email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1SSN"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Social Security # *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="XXX-XX-XXXX" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1Ownership"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ownership % *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="%" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1Cell"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cell Phone *</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1HomePhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Home Phone</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner1HomeAddress"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Home Address *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="owner2" className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground mb-4">Optional - Add second owner/officer if applicable</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="owner2Name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2Title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2Email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2SSN"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Social Security #</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="XXX-XX-XXXX" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2Ownership"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ownership %</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="%" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2Cell"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cell Phone</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2HomePhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Home Phone</FormLabel>
                        <FormControl>
                          <Input {...field} type="tel" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="owner2HomeAddress"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Home Address</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <FormField
              control={form.control}
              name="additionalNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} placeholder="Any additional information you'd like to share..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agreeToTerms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="mt-1"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <Label>
                      I agree to the terms and conditions and authorize a credit check *
                    </Label>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
